// Generisches Punktmenü (Kebab/Drei-Punkte-Dropdown).
//
// Aufklappbares Aktions-Menü für Desktop. Trigger ist ein dezenter
// Drei-Punkte-Button; Klick öffnet ein Panel mit Aktions-Einträgen.
// Schließt per Klick außerhalb, ESC oder nach Auswahl einer Aktion.
//
// Stil-Muster gespiegelt von NotificationBell.jsx (Emil-Kowalski-Prinzipien):
// Panel entsteht aus der oberen Kante, transform-origin top-right, federt per
// Spring auf; Einträge gestaffelt; Exit schneller als Enter; reduced-motion
// respektiert. Editorial: kein rot-getöntes Panel/Border/Glow — „danger"-
// Einträge tragen nur roten Text/Stroke.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { F, DUR } from '../../styles/theme';
import { Icon, Icons } from './Icons';

/**
 * @param {Array<{key:string,label:string,icon?:any,danger?:boolean,disabled?:boolean,onClick:()=>void}>} actions
 * @param {'right'|'left'} align     Panel-Ausrichtung relativ zum Trigger
 * @param {string} title            aria-label / title des Triggers
 * @param {number} buttonSize       Trigger-Kantenlänge (px)
 */
export default function KebabMenu({ actions = [], align = 'right', title = 'Aktionen', buttonSize = 36 }) {
  const { T } = useTheme();
  const reduce = useReducedMotion();
  const accent = T.signal || T.red || '#E4544A';
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const panelVariants = reduce
    ? {
        hidden: { opacity: 0 },
        show:   { opacity: 1, transition: { duration: 0.12 } },
        exit:   { opacity: 0, transition: { duration: 0.1 } },
      }
    : {
        hidden: { opacity: 0, y: -12, scaleY: 0.85, scaleX: 0.98 },
        show: {
          opacity: 1, y: 0, scaleY: 1, scaleX: 1,
          transition: {
            type: 'spring', stiffness: 470, damping: 34, mass: 0.9,
            opacity: { duration: DUR.fast / 1000, ease: 'easeOut' },
            delayChildren: 0.06, staggerChildren: 0.03,
          },
        },
        exit: { opacity: 0, y: -8, scaleY: 0.92, transition: { duration: 0.13, ease: [0.4, 0, 1, 1] } },
      };
  const itemVariants = reduce
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 520, damping: 36 } },
      };

  const surfaceHi = T.surfaceHi || 'rgba(255,255,255,0.04)';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseEnter={(e) => { if (!open) { e.currentTarget.style.color = T.text; e.currentTarget.style.background = surfaceHi; } }}
        onMouseLeave={(e) => { if (!open) { e.currentTarget.style.color = T.sub; e.currentTarget.style.background = 'transparent'; } }}
        style={{
          width: buttonSize, height: buttonSize, borderRadius: 10,
          background: open ? surfaceHi : 'transparent', border: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: open ? T.text : T.sub, cursor: 'pointer', flexShrink: 0,
          transition: `color ${DUR.press}ms ease, background ${DUR.press}ms ease`,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Icon d={Icons.kebab} size={18} stroke="currentColor" sw={1.8} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="kebab-panel"
            role="menu"
            variants={panelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            style={{
              position: 'absolute', top: 'calc(100% + 8px)',
              [align]: 0,
              zIndex: 9998,
              transformOrigin: align === 'right' ? 'top right' : 'top left',
              minWidth: 224,
              maxWidth: 'calc(100vw - 48px)',
              display: 'flex', flexDirection: 'column',
              background: T.surface,
              border: `1px solid ${T.line}`,
              borderRadius: 16,
              overflow: 'hidden',
              // Calm: weicher, warmer Schatten statt hartem Schwarz.
              boxShadow: T.isDark
                ? '0 18px 48px -14px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
                : '0 18px 44px -14px rgba(60,48,28,0.26), 0 3px 10px rgba(60,48,28,0.08)',
            }}
          >
            {actions.map((a, i) => {
              const color = a.danger ? accent : T.text;
              return (
                <motion.button
                  key={a.key}
                  role="menuitem"
                  variants={itemVariants}
                  disabled={a.disabled}
                  onClick={() => { if (a.disabled) return; a.onClick?.(); setOpen(false); }}
                  onMouseEnter={(e) => { if (!a.disabled) e.currentTarget.style.background = surfaceHi; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  style={{
                    display: 'flex', alignItems: 'center',
                    width: '100%', textAlign: 'left',
                    padding: '13px 16px', background: 'transparent',
                    border: 'none', borderTop: i ? `1px solid ${T.lineSoft || T.line}` : 'none',
                    color, fontFamily: F.family, fontSize: 13.5, fontWeight: 600,
                    letterSpacing: '-0.01em',
                    cursor: a.disabled ? 'not-allowed' : 'pointer',
                    opacity: a.disabled ? 0.4 : 1,
                    transition: 'background 130ms ease',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {/* Calm: reiner Schriftzug, keine Icons (konsistent mit Bearbeiten/Löschen). */}
                  {a.label}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
